import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { streamHelpChat, type HelpFailureKind, type HelpMessage } from '../api/help.ts';
import { useAiDisclosure } from './ai-disclosure.tsx';
import { helpDraftScopeFor, useSession } from '../state/session.tsx';
import {
  captureHelpDraftScope,
  readHelpDraft,
  writeHelpDraft,
  type HelpDraftScope,
} from '../state/help-draft-store.ts';
import { useAppConfig } from './turnstile.tsx';
import { Icon } from './icons.tsx';
import { classes, Spinner } from './ui.tsx';
import { FounderContactLine } from './founder-contact.tsx';
import { isHelpPath } from './navigation-path.ts';

/** Embedded Help chat. Reuses the existing per-account transcript and SSE service. */

const STORAGE_KEY_PREFIX = 'cqb_help_chat:';

/** The server refuses conversations past two dozen messages; trim to match. */
const MAX_SENT_MESSAGES = 24;

/** The server refuses any message longer than this; clamp history to match. */
const MAX_SENT_CHARS = 4000;

/**
 * Per-user key, and other users' entries purged on read: a shared browser
 * where one person signs out and another signs in must not hand the second
 * person the first one's questions — or silently resend them to the server
 * as conversation history.
 */
function storedMessages(storageKey: string): HelpMessage[] {
  try {
    for (const key of Object.keys(window.sessionStorage)) {
      if (key.startsWith(STORAGE_KEY_PREFIX) && key !== storageKey) {
        window.sessionStorage.removeItem(key);
      }
    }
    const raw = window.sessionStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    const entries = Array.isArray(parsed) ? parsed : (parsed as { messages?: unknown })?.messages;
    if (!Array.isArray(entries)) return [];
    return entries.filter(
      (entry): entry is HelpMessage =>
        typeof entry === 'object' &&
        entry !== null &&
        ((entry as HelpMessage).role === 'user' || (entry as HelpMessage).role === 'assistant') &&
        typeof (entry as HelpMessage).content === 'string',
    );
  } catch {
    return [];
  }
}

function storedFailure(storageKey: string): HelpFailureKind | null {
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(storageKey) ?? 'null') as {
      failure?: HelpFailureKind;
    } | null;
    const failure = parsed?.failure;
    return failure && ['error', 'rateLimited', 'unavailable', 'providerBudget', 'costPolicy'].includes(failure)
      ? failure
      : null;
  } catch {
    return null;
  }
}

function storeMessages(storageKey: string, messages: HelpMessage[], failure: HelpFailureKind | null = null): void {
  try {
    if (messages.length === 0) window.sessionStorage.removeItem(storageKey);
    else window.sessionStorage.setItem(storageKey, JSON.stringify(failure ? { messages, failure } : messages));
  } catch {
    // Storage full or blocked: the chat still works, it just forgets on nav.
  }
}

/**
 * What actually gets resent as history: displayed messages are kept whole,
 * but the wire copy must satisfy the server's own message schema or every
 * later turn 400s — a permanently wedged conversation. So: empty messages
 * dropped, content clamped to the server's per-message cap (losing the tail
 * of a long past answer costs context, not correctness), the last
 * MAX_SENT_MESSAGES kept, opened at a whole turn because the model API
 * requires the first message to be the user's.
 */
function sendWindow(messages: HelpMessage[]): HelpMessage[] {
  const bounded = messages
    .map((message) => ({ ...message, content: clampForWire(message.content) }))
    .filter((message) => message.content !== '');
  const window = bounded.slice(-MAX_SENT_MESSAGES);
  const firstUser = window.findIndex((message) => message.role === 'user');
  return firstUser <= 0 ? window : window.slice(firstUser);
}

/**
 * Trim BEFORE clamping — a message whose first 4000 units were whitespace
 * would otherwise ship whitespace-only and fail the server's `.trim().min(1)`
 * — and never cut a surrogate pair in half: the server's schema would pass a
 * lone high surrogate, but the model API rejects it, which would wedge every
 * later turn on the same stored message.
 */
function clampForWire(content: string): string {
  let clamped = content.trim().slice(0, MAX_SENT_CHARS);
  const last = clamped.charCodeAt(clamped.length - 1);
  if (last >= 0xd800 && last <= 0xdbff) clamped = clamped.slice(0, -1);
  return clamped;
}

/**
 * One panel per person at the keyboard: the account, its company, and — when
 * an operator is signed in as that account from /admin — the operator. The
 * session can change under a mounted layout (another tab signs out and
 * someone else signs in on the same browser, a support session starts or
 * ends), and the panel's state was captured for the person before: their
 * thread on screen, persisted under the next person's storage key, resent as
 * the next person's history, and an answer still streaming in. Keying by that
 * identity remounts the panel instead, which aborts that request (see the
 * unmount effect below) and reads the next person's own stored thread.
 */
export default function HelpPanel(props: { showFounderContact?: boolean }) {
  const { user, company, currentLocation, impersonatedBy } = useSession();
  const draftScope = captureHelpDraftScope(
    helpDraftScopeFor(user, company, currentLocation?.id ?? null, impersonatedBy),
  );
  const scope = JSON.stringify([user?.id ?? null, company?.id ?? null, impersonatedBy, draftScope]);
  // A support session keeps its own thread: the operator's questions are not
  // the account holder's, and must neither land in nor read theirs.
  const storageKey = `${STORAGE_KEY_PREFIX}${user?.id ?? 'anonymous'}${impersonatedBy ? `:support:${impersonatedBy}` : ''}`;
  // Set by a panel that unmounts with focus inside it, so its successor can
  // put that focus on its heading instead of dropping it on the body.
  const focusHandoffRef = useRef(false);
  const visitedRef = useRef(false);
  const activeScopeRef = useRef(scope);
  activeScopeRef.current = scope;
  const activeStorageKeyRef = useRef(storageKey);
  activeStorageKeyRef.current = storageKey;
  const turnHandoffRef = useRef<TurnHandoff | null>(null);
  return (
    <AccountHelpPanel
      key={scope}
      focusHandoffRef={focusHandoffRef}
      visitedRef={visitedRef}
      activeScopeRef={activeScopeRef}
      activeStorageKeyRef={activeStorageKeyRef}
      turnHandoffRef={turnHandoffRef}
      scope={scope}
      storageKey={storageKey}
      draftScope={draftScope}
      {...props}
    />
  );
}

/**
 * A turn cut short by a remount that keeps the same transcript — switching
 * location from the header while an answer streams. The successor's first
 * render can still read storage before the retiring panel persists, so the
 * partial answer is handed over in a layout effect and shown with Try again.
 */
interface TurnHandoff {
  storageKey: string;
  messages: HelpMessage[];
}

function AccountHelpPanel({
  showFounderContact = true,
  focusHandoffRef,
  visitedRef,
  activeScopeRef,
  activeStorageKeyRef,
  turnHandoffRef,
  scope,
  storageKey,
  draftScope,
}: {
  showFounderContact?: boolean | undefined;
  focusHandoffRef: { current: boolean };
  visitedRef: { current: boolean };
  activeScopeRef: { current: string };
  activeStorageKeyRef: { current: string };
  turnHandoffRef: { current: TurnHandoff | null };
  scope: string;
  storageKey: string;
  draftScope: HelpDraftScope | null;
}) {
  const { t, i18n } = useTranslation();
  const disclosure = useAiDisclosure();
  const location = useLocation();
  const titleId = useId();
  const { user } = useSession();
  const allowedOrigins = useAppConfig().data?.helpLinkOrigins ?? [];
  const [messages, setMessages] = useState<HelpMessage[]>(() => storedMessages(storageKey));
  const [draft, setDraft] = useState(() => readHelpDraft(draftScope));
  const draftRef = useRef(draft);
  draftRef.current = draft;
  /** The assistant's answer as it streams; null when nothing is in flight. */
  const [streaming, setStreaming] = useState<string | null>(null);
  const [failure, setFailure] = useState<HelpFailureKind | null>(() => storedFailure(storageKey));
  const [sending, setSending] = useState(false);
  const failureRef = useRef(failure);
  failureRef.current = failure;
  const busy = sending || streaming !== null;
  const sendLockRef = useRef<symbol | null>(null);
  const followRef = useRef(true);
  const partialRef = useRef<HelpMessage[]>(messages);

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const logRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  /**
   * Bumped by clear(): a turn that was in flight when the conversation was
   * cleared must not write its captured history back over the empty one.
   */
  const generationRef = useRef(0);

  // Layout so a predecessor's cleanup can hand over before paint and before
  // the store effect would otherwise write the incomplete first render.
  useLayoutEffect(() => {
    const handoff = turnHandoffRef.current;
    turnHandoffRef.current = null;
    if (handoff && handoff.storageKey === storageKey) {
      partialRef.current = handoff.messages;
      setMessages(handoff.messages);
      setFailure('error');
      // Write now so a mount store effect with the first render's empty read
      // cannot leave storage without the partial and failure marker.
      storeMessages(storageKey, handoff.messages, 'error');
    }
    return () => {
      // Location remount: same transcript, new scope. Leave the interrupted
      // turn for the successor (applied in its layout effect above).
      if (abortRef.current && activeScopeRef.current !== scope && activeStorageKeyRef.current === storageKey) {
        turnHandoffRef.current = { storageKey, messages: partialRef.current };
      }
    };
  }, []);
  useEffect(() => storeMessages(storageKey, messages, failure), [storageKey, messages, failure]);
  useEffect(() => writeHelpDraft(draftScope, draft), [draftScope, draft]);

  // The shell gives Help the remaining viewport. VisualViewport also accounts
  // for keyboards that pan or shrink only the visible browser area: a keyboard
  // that pans moves the visible area down the page (offsetTop), so the shell
  // moves with it — sized alone, its header would sit above the visible area.
  // Pinch zoom (scale ≠ 1) is the reader's own panning; leave the page put.
  useLayoutEffect(() => {
    const viewport = window.visualViewport;
    const shell = panelRef.current?.closest<HTMLElement>('.app-shell');
    if (!viewport || !shell) return;
    const fit = () => {
      const unzoomed = viewport.scale === 1;
      shell.style.setProperty('--help-viewport-height', `${unzoomed ? viewport.height : window.innerHeight}px`);
      shell.style.setProperty('--help-viewport-top', `${unzoomed ? Math.max(0, viewport.offsetTop) : 0}px`);
    };
    fit();
    viewport.addEventListener('resize', fit);
    viewport.addEventListener('scroll', fit);
    return () => {
      viewport.removeEventListener('resize', fit);
      viewport.removeEventListener('scroll', fit);
      shell.style.removeProperty('--help-viewport-height');
      shell.style.removeProperty('--help-viewport-top');
    };
  }, []);

  // Follow new text only while the reader is at the end. Reading an earlier
  // answer must not be interrupted by the next streamed chunk.
  useEffect(() => {
    const log = logRef.current;
    if (log && followRef.current) log.scrollTop = log.scrollHeight;
  }, [messages, streaming]);

  // An in-flight answer must not outlive the panel's page — nor a send still
  // waiting on the AI notice, which has no request to abort yet.
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    const persist = () => {
      // Same person/transcript: keep an interrupted answer even when a
      // location remount changed scope. A different person uses another key
      // and must not receive this write. Drafts stay location-scoped.
      if (activeStorageKeyRef.current === storageKey) {
        storeMessages(storageKey, partialRef.current, abortRef.current ? 'error' : failureRef.current);
      }
      if (activeScopeRef.current === scope) {
        writeHelpDraft(draftScope, draftRef.current);
      }
    };
    // A document refresh does not run React's unmount cleanup.
    window.addEventListener('pagehide', persist);
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
      persist();
      window.removeEventListener('pagehide', persist);
    };
  }, []);

  // A layout cleanup runs while this panel's DOM is still in the document, so
  // it can see whether focus was inside; the successor panel then moves it to
  // its heading.
  useLayoutEffect(() => {
    if (!visitedRef.current || focusHandoffRef.current) {
      visitedRef.current = true;
      focusHandoffRef.current = false;
      headingRef.current?.focus({ preventScroll: true });
    }
    return () => {
      // Preserve focus across a change of account only when Help held it.
      const focused = document.activeElement;
      if (panelRef.current?.contains(focused)) focusHandoffRef.current = true;
    };
  }, [focusHandoffRef]);

  async function send(question: string, history = messages, { preserveDraft = false } = {}) {
    const content = question.trim();
    if (!content || sendLockRef.current) return;
    const lock = Symbol('help turn');
    sendLockRef.current = lock;
    setSending(true);
    const startedAt = generationRef.current;
    try {
      await disclosure.ensure(true);
      if (!mountedRef.current || generationRef.current !== startedAt) return;
      const sent = [...history, { role: 'user' as const, content }];
      const generation = ++generationRef.current;
      followRef.current = true;
      partialRef.current = sent;
      setMessages(sent);
      if (!preserveDraft) setDraft('');
      setFailure(null);
      setStreaming('');
      const controller = new AbortController();
      abortRef.current = controller;
      const result = await streamHelpChat(
        {
          messages: sendWindow(sent),
          language: i18n.language?.toLowerCase().startsWith('es') ? 'es' : 'en',
          platform: 'web',
          page: helpContext(location.pathname, user?.id),
        },
        (delta) => {
          if (!mountedRef.current || generationRef.current !== generation) return;
          const previous = partialRef.current.at(-1);
          const answer = (previous?.role === 'assistant' ? previous.content : '') + delta;
          partialRef.current = [...sent, { role: 'assistant', content: answer }];
          setStreaming(answer);
        },
        controller.signal,
      );
      if (!mountedRef.current || generationRef.current !== generation) return;
      abortRef.current = null;
      setStreaming(null);
      const text = result.ok ? result.text : result.partialText;
      partialRef.current = text ? [...sent, { role: 'assistant', content: text }] : sent;
      setMessages(partialRef.current);
      if (!result.ok) setFailure(result.kind);
    } catch {
      // A dismissed disclosure does not send a question.
    } finally {
      if (sendLockRef.current === lock) {
        sendLockRef.current = null;
        if (mountedRef.current) setSending(false);
      }
    }
  }

  function stop() {
    generationRef.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
    sendLockRef.current = null;
    setSending(false);
    setStreaming(null);
    setMessages(partialRef.current);
    setFailure('error');
  }

  function retry() {
    const lastUser = messages.map((message) => message.role).lastIndexOf('user');
    if (lastUser >= 0) void send(messages[lastUser]!.content, messages.slice(0, lastUser), { preserveDraft: true });
  }

  function onComposerKeyDown(event: ReactKeyboardEvent<HTMLTextAreaElement>) {
    // isComposing: the Enter that confirms an IME candidate (Japanese,
    // Chinese, Korean …) is composition, not Send — submitting there would
    // fire half-written questions.
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      void send(draft);
    }
  }

  function clear() {
    generationRef.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
    sendLockRef.current = null;
    partialRef.current = [];
    setSending(false);
    followRef.current = true;
    setMessages([]);
    setStreaming(null);
    setFailure(null);
    setDraft('');
    inputRef.current?.focus();
  }

  const failureText =
    failure === 'providerBudget'
      ? t('help.providerBudget')
      : failure === 'costPolicy'
        ? t('help.costPolicy')
        : failure === 'rateLimited'
          ? t('help.rateLimited')
          : failure === 'unavailable'
            ? t('help.unavailable')
            : failure === 'error'
              ? t('help.error')
              : null;

  const suggested = [t('help.suggested1'), t('help.suggested2'), t('help.suggested3'), t('help.suggested4')];

  return (
    <section ref={panelRef} aria-labelledby={titleId} className="help-page print:hidden">
      {disclosure.dialog}
      <header className="flex shrink-0 items-center justify-between gap-3 border-b border-[color:var(--color-line)] bg-white px-4 py-3 sm:px-6 sm:py-4">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[color:var(--color-brand-soft)] text-[color:var(--color-brand)]">
            <Icon name="help" size={24} />
          </span>
          <div>
            <h1 ref={headingRef} tabIndex={-1} id={titleId} className="text-xl font-bold focus:outline-none">
              {t('help.title')}
            </h1>
            <p className="text-sm text-[color:var(--color-muted)]">{t('help.subtitle')}</p>
          </div>
        </div>
        {messages.length > 0 ? (
          <button type="button" onClick={clear} className="btn btn-secondary shrink-0 text-sm">
            {t('help.clear')}
          </button>
        ) : null}
      </header>
      <div
        ref={logRef}
        role="log"
        aria-label={t('help.conversation')}
        aria-busy={busy}
        onScroll={() => {
          const log = logRef.current;
          if (log) followRef.current = log.scrollHeight - log.scrollTop - log.clientHeight < 64;
        }}
        className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-4 py-5 text-sm [overflow-wrap:anywhere] sm:px-6 sm:text-base"
      >
        {messages.length === 0 && streaming === null ? (
          <div className="mx-auto max-w-2xl py-3 sm:py-8">
            <div className="rounded-2xl border border-[#e8dfcf] bg-[#faf6ed] p-5 sm:p-7">
              <h2 className="text-lg font-semibold">{t('help.welcome')}</h2>
              <p className="mt-2 leading-relaxed text-[color:var(--color-muted)]">{t('help.intro')}</p>
            </div>
            <p className="mt-6 mb-3 text-sm font-semibold text-[color:var(--color-muted)]">{t('help.suggestions')}</p>
            <div className="grid gap-2 sm:grid-cols-2">
              {suggested.map((question) => (
                <button
                  key={question}
                  type="button"
                  disabled={busy}
                  onClick={() => void send(question)}
                  className="min-h-12 rounded-xl border border-[color:var(--color-line)] bg-white px-4 py-3 text-start text-sm hover:border-[color:var(--color-brand)] hover:bg-[color:var(--color-brand-soft)] focus-visible:outline-2 focus-visible:outline-[color:var(--color-brand)] disabled:opacity-50"
                >
                  {question}
                </button>
              ))}
            </div>
          </div>
        ) : null}
        {messages.map((message, index) => (
          <ChatBubble key={index} message={message} allowedOrigins={allowedOrigins} />
        ))}
        {streaming !== null ? (
          streaming === '' ? (
            <div role="status" className="flex items-center gap-2 text-[color:var(--color-muted)]">
              <Spinner /> {t('help.thinking')}
            </div>
          ) : (
            <ChatBubble message={{ role: 'assistant', content: streaming }} allowedOrigins={allowedOrigins} />
          )
        ) : null}
        {failureText ? (
          <div
            role="alert"
            className="rounded-xl border border-[color:var(--color-danger)]/20 bg-[color:var(--color-danger-soft)] p-3 text-sm"
          >
            <p>{failureText}</p>
            {failure === 'error' || failure === 'rateLimited' || failure === 'unavailable' ? (
              <button type="button" onClick={retry} disabled={busy} className="mt-2 min-h-11 font-semibold underline">
                {t('help.retry')}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
      <footer className="shrink-0 border-t border-[color:var(--color-line)] bg-white p-3 sm:px-6 sm:py-4">
        <form
          className="flex items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            void send(draft);
          }}
        >
          <textarea
            ref={inputRef}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={onComposerKeyDown}
            rows={2}
            maxLength={4000}
            placeholder={t('help.placeholder')}
            aria-label={t('help.placeholder')}
            className="max-h-28 min-h-12 min-w-0 flex-1 resize-none rounded-xl border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-3 py-2 text-base focus:outline-2 focus:outline-[color:var(--color-brand)]"
          />
          {streaming !== null ? (
            <button type="button" onClick={stop} className="btn btn-secondary min-h-12 text-sm">
              {t('help.stop')}
            </button>
          ) : (
            <button
              type="submit"
              disabled={busy || draft.trim() === ''}
              className="btn btn-primary min-h-12 px-4 text-sm disabled:opacity-50"
            >
              {t('help.send')}
            </button>
          )}
        </form>
        {showFounderContact ? <FounderContactLine className="mt-2 text-xs text-[color:var(--color-muted)]" /> : null}
        <p className="mt-1 text-xs text-[color:var(--color-muted)]">{t('help.disclaimer')}</p>
      </footer>
    </section>
  );
}

/** Keep the last work page as context when navigation opens Help directly. */
function helpContext(path: string, userId: string | undefined): string {
  if (!isHelpPath(path)) return path.slice(0, 120);
  try {
    const saved = window.sessionStorage.getItem(`cqb_help_context:${userId ?? 'anonymous'}`);
    return saved?.startsWith('/') && !saved.startsWith('//') ? saved.slice(0, 120) : '/help';
  } catch {
    return '/help';
  }
}

function ChatBubble({ message, allowedOrigins }: { message: HelpMessage; allowedOrigins: readonly string[] }) {
  if (message.role === 'user') {
    return (
      <p className="ms-8 rounded-xl rounded-br-sm bg-[color:var(--color-brand)]/10 px-3 py-2 whitespace-pre-wrap">
        {message.content}
      </p>
    );
  }
  return (
    <div className="me-4 space-y-2 rounded-xl rounded-bl-sm bg-[color:var(--color-surface)] px-3 py-2">
      {renderMarkdown(message.content, allowedOrigins)}
    </div>
  );
}

/**
 * The safe sliver of Markdown a help answer uses: paragraphs, bullet and
 * numbered lists, links, bold, inline code. Rendered as React elements —
 * never as HTML — so the model's output cannot inject markup, and links only
 * ever point at http(s) URLs in a new tab. Anything fancier degrades to its
 * literal text, which for a support answer is the right failure.
 */
function renderMarkdown(text: string, allowedOrigins: readonly string[]): ReactNode[] {
  const blocks: ReactNode[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;

  const flushList = () => {
    if (!list) return;
    const items = list.items.map((item, index) => <li key={index}>{renderInline(item, allowedOrigins)}</li>);
    blocks.push(
      list.ordered ? (
        <ol key={blocks.length} className="list-decimal space-y-1 ps-5">
          {items}
        </ol>
      ) : (
        <ul key={blocks.length} className="list-disc space-y-1 ps-5">
          {items}
        </ul>
      ),
    );
    list = null;
  };

  for (const rawLine of text.split('\n')) {
    const line = rawLine.trim();
    const bullet = /^[-*]\s+(.*)$/.exec(line);
    const numbered = /^\d+[.)]\s+(.*)$/.exec(line);
    if (bullet || numbered) {
      const ordered = Boolean(numbered);
      if (!list || list.ordered !== ordered) {
        flushList();
        list = { ordered, items: [] };
      }
      list.items.push((bullet ?? numbered)![1]!);
    } else if (line === '') {
      flushList();
    } else {
      flushList();
      blocks.push(<p key={blocks.length}>{renderInline(line, allowedOrigins)}</p>);
    }
  }
  flushList();
  return blocks;
}

const INLINE_PATTERN = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|\*\*([^*]+)\*\*|`([^`]+)`/g;

/** Whether a model-emitted URL may become a clickable link. */
function linkAllowed(url: string, allowedOrigins: readonly string[]): boolean {
  try {
    return allowedOrigins.includes(new URL(url).origin);
  } catch {
    return false;
  }
}

function renderInline(text: string, allowedOrigins: readonly string[]): ReactNode[] {
  const parts: ReactNode[] = [];
  let cursor = 0;
  for (const match of text.matchAll(INLINE_PATTERN)) {
    if (match.index > cursor) parts.push(text.slice(cursor, match.index));
    if (match[1] !== undefined && match[2] !== undefined) {
      // Only the deployment's own documentation origins become anchors —
      // the model's output is untrusted, and a hallucinated or injected URL
      // must not arrive dressed as a help link. Everything else keeps its
      // link TEXT (never the raw URL) as plain words.
      if (linkAllowed(match[2], allowedOrigins)) {
        parts.push(
          <a
            key={parts.length}
            href={match[2]}
            target="_blank"
            rel="noreferrer noopener"
            className={classes('font-medium text-[color:var(--color-brand)] underline')}
          >
            {match[1]}
          </a>,
        );
      } else {
        parts.push(match[1]);
      }
    } else if (match[3] !== undefined) {
      parts.push(<strong key={parts.length}>{match[3]}</strong>);
    } else if (match[4] !== undefined) {
      parts.push(
        <code key={parts.length} className="rounded bg-[color:var(--color-line)]/50 px-1 font-mono text-[0.85em]">
          {match[4]}
        </code>,
      );
    }
    cursor = match.index + match[0].length;
  }
  if (cursor < text.length) parts.push(text.slice(cursor));
  return parts;
}
